import { IMeasureControl, Maybe, QueryType } from '../apollo/graphqlSchema';
import {
  IAliasableChartData,
  IAliasableMeasureControlsChartData,
  IAliasableSlice,
  IHasAlias,
  IMeasurableChartData,
  IMeasurableSliceColumn,
  IMeasureSlice,
  IParsedAliasReplacements,
  IParsedChartData,
} from '../../components/Chart/interfaces';
import produce from 'immer';
import { generateLinesMetaFromColumns } from './transformExpressionData';
import { GraphQLUserAliasId, isNotNil } from '../types';
import { generateLinesMetaFromColumnsFunnel } from './transformFunnelData';
import { isOnlyOverallSlices } from '../../components/Chart/utils';
import { generateLinesMetaForRetentionTable } from './transformRetentionData';
import { UnreachableCaseError } from 'ts-essentials';

export type IAliasedName = IHasAlias & { name: string };

export const getAliasedName = (
  original: Maybe<string>,
  userAliasId: Maybe<GraphQLUserAliasId>,
  aliasReplacements: Maybe<IParsedAliasReplacements>,
): IAliasedName => {
  // special case for funnel step
  if (!original) {
    return { hasUserAlias: false, name: '' };
  }
  if (!userAliasId || !aliasReplacements) {
    return { hasUserAlias: false, name: original };
  }

  if (aliasReplacements.hasOwnProperty(userAliasId)) {
    return { hasUserAlias: true, name: aliasReplacements[userAliasId] };
  } else {
    return { hasUserAlias: false, name: original };
  }
};

const updateSliceName = (
  draftSlice: IAliasableSlice,
  aliasReplacements: IParsedAliasReplacements,
) => {
  const userAlias = draftSlice.userAlias;
  const defaultName = draftSlice.name;
  switch (userAlias.__typename) {
    case 'UserAliasSliceBreakdown': {
      let hasGroupUserAlias = false;
      for (
        let i = 0;
        i < draftSlice.hint.length && i < userAlias.groupBys.length;
        i++
      ) {
        const groupByAliasId = userAlias.groupBys[i];
        const hint = draftSlice.hint[i];
        const groupByAliasedName = getAliasedName(
          hint.value,
          groupByAliasId,
          aliasReplacements,
        );
        hint.value = groupByAliasedName.name;
        hasGroupUserAlias =
          hasGroupUserAlias || groupByAliasedName.hasUserAlias;
      }
      const defaultNameFromSliceHints = draftSlice.hint
        .map((h) => h.value)
        .join(' - ');
      const aliasedName = getAliasedName(
        defaultNameFromSliceHints,
        userAlias.aliasId,
        aliasReplacements,
      );
      draftSlice.name = aliasedName.name;
      draftSlice.hasUserAlias = hasGroupUserAlias || aliasedName.hasUserAlias;
      break;
    }
    case 'UserAliasSliceBase': {
      const aliasedName = getAliasedName(
        defaultName,
        userAlias.aliasId,
        aliasReplacements,
      );
      draftSlice.hint[0].value = aliasedName.name;
      draftSlice.name = aliasedName.name;
      draftSlice.hasUserAlias = aliasedName.hasUserAlias;
      break;
    }
    case 'UserAliasSliceNone':
      break;
    default:
      throw new UnreachableCaseError(userAlias);
  }
};

const getColumnName = (
  column: IMeasurableSliceColumn,
  measures: IMeasureControl[],
  slices: IMeasureSlice[],
): string => {
  const measureName = measures.find(
    (measure) => measure.key === column.measureKey,
  )?.name;
  if (isOnlyOverallSlices(slices)) {
    // do not add slice name prefix if the analysis does not have breakdowns
    return measureName ?? '';
  } else {
    const sliceName = slices.find(
      (slice) => slice.key === column.sliceKey,
    )?.name;
    return [sliceName, measureName].filter(isNotNil).join(' - ');
  }
};

const updateMeasureAndSliceAliases = (draft: IMeasurableChartData) => {
  const { measureControls, slices, columns } = draft;
  updateMeasureControlsAliases(draft);
  updateSliceAlias(draft);
  columns.forEach((column) => {
    column.name = getColumnName(column, measureControls, slices);
    column.sliceName =
      slices.find((slice) => slice.key === column.sliceKey)?.name ?? '';
    column.measureName =
      measureControls.find((measure) => measure.key === column.measureKey)
        ?.name ?? '';
  });
};

const updateMeasureControlsAliases = (
  draft: IAliasableMeasureControlsChartData,
) => {
  const { measureControls, aliasReplacements } = draft;
  measureControls.forEach((mc) => {
    const aliasedName = getAliasedName(
      mc.name,
      mc.userAlias.aliasId,
      aliasReplacements,
    );
    mc.name = aliasedName.name;
    mc.hasUserAlias = aliasedName.hasUserAlias;
  });
};

const updateSliceAlias = (
  draft: IAliasableChartData & { slices: IAliasableSlice[] },
) => {
  const { slices, aliasReplacements } = draft;
  slices.forEach((slice) => {
    updateSliceName(slice, aliasReplacements);
  });
};

const applyAliasReplacements = (
  parsedChartData: IParsedChartData,
): IParsedChartData =>
  produce(parsedChartData, (draft) => {
    switch (draft.queryType) {
      case QueryType.Expression:
        updateMeasureAndSliceAliases(draft);
        draft.linesMeta = generateLinesMetaFromColumns(
          draft.columns,
          draft.measureControls,
          isOnlyOverallSlices(draft.slices), // if overall is the only slice, there is no breakdown in expression
          draft.linesMeta[draft.measureControls[0].key]?.measureLabel,
        );
        break;
      case QueryType.Funnel:
        updateMeasureAndSliceAliases(draft);
        draft.linesMeta = generateLinesMetaFromColumnsFunnel(
          draft.columns,
          draft.funnelModeSlices,
          draft.measureControls,
          isOnlyOverallSlices(draft.slices), // if overall is the only slice, there is no breakdown in expression
        );
        break;
      case QueryType.Retention:
        updateSliceAlias(draft);
        draft.linesMeta = generateLinesMetaForRetentionTable(
          draft.table,
          draft.slices,
        );
        break;
      case QueryType.Path:
        updateSliceAlias(draft);
        break;
      case QueryType.DataTable:
        updateMeasureControlsAliases(draft);
        break;
      case QueryType.CohortSample:
      case QueryType.Prediction:
      case QueryType.Significance:
        // aliases are not applicable
        break;
      default:
        throw new UnreachableCaseError(draft);
    }
  });

export default applyAliasReplacements;
